import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, ExternalLink, Trash2, Kanban, Sparkles, RefreshCw, Target } from "lucide-react";
import { useCurrency, useCurrencyChangeListener } from "@/context/CurrencyContext";
import Money from "@/components/Money";

const STATUSES = [
  { key: "applied", label: "Applied", color: "#0F52BA" },
  { key: "phone_screen", label: "Phone Screen", color: "#7c3aed" },
  { key: "interview", label: "Interview", color: "#F59E0B" },
  { key: "offer", label: "Offer", color: "#047857" },
  { key: "rejected", label: "Rejected", color: "#DC2626" },
];

export default function Jobs() {
  const [tab, setTab] = useState("tracker");
  return (
    <div className="p-10" data-testid="jobs-page">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Module 05</div>
        <h1 className="font-display font-bold text-5xl tracking-tight">Jobs</h1>
      </div>
      <div className="inline-flex gap-px bg-border border border-border mb-8">
        <button data-testid="tab-tracker" onClick={()=>setTab("tracker")} className={`px-5 py-2.5 text-sm flex items-center gap-2 ${tab==="tracker"?"bg-primary text-foreground":"bg-card text-muted-foreground hover:text-foreground"}`}><Kanban size={14}/> Tracker</button>
        <button data-testid="tab-matches" onClick={()=>setTab("matches")} className={`px-5 py-2.5 text-sm flex items-center gap-2 ${tab==="matches"?"bg-primary text-foreground":"bg-card text-muted-foreground hover:text-foreground"}`}><Sparkles size={14}/> AI Matches</button>
      </div>
      {tab === "tracker" ? <Tracker/> : <Matches/>}
    </div>
  );
}

function Tracker() {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company: "", role: "", location: "", salary: "", status: "applied", notes: "", job_url: "" });
  const { currency, ready } = useCurrency();
  useEffect(() => { load(); }, []);
  const load = () => api.get("/jobs").then(r => setJobs(r.data));

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/jobs", { ...form, salary: form.salary ? Number(form.salary) : null });
      setForm({ company: "", role: "", location: "", salary: "", status: "applied", notes: "", job_url: "" });
      setOpen(false); load(); toast.success("Job added");
    } catch { toast.error("Add failed"); }
  };
  const moveStatus = async (job, newStatus) => { try { await api.put(`/jobs/${job.id}`, { status: newStatus }); load(); } catch { toast.error("Update failed"); } };
  const remove = async (id) => { if (!window.confirm("Delete this job?")) return; try { await api.delete(`/jobs/${id}`); load(); toast.success("Deleted"); } catch { toast.error("Delete failed"); } };

  return (
    <>
      <div className="flex justify-end mb-4">
        <button data-testid="add-job-btn" onClick={()=>setOpen(true)} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-4 py-2.5 text-sm flex items-center gap-2"><Plus size={14}/> Add job</button>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
        {STATUSES.map(col => {
          const items = jobs.filter(j => j.status === col.key);
          return (
            <div key={col.key} className="kanban-col p-4 shrink-0" data-testid={`kanban-col-${col.key}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2"><div className="w-2 h-2" style={{ background: col.color }}/><div className="font-display font-bold text-sm tracking-tight">{col.label}</div></div>
                <div className="font-mono text-xs text-muted-foreground">{items.length}</div>
              </div>
              <div className="space-y-2 min-h-[100px]">
                {items.map(j => (
                  <div key={j.id} className="bg-card border border-border p-3 group" data-testid={`job-card-${j.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0"><div className="font-display font-bold text-sm truncate">{j.role}</div><div className="text-xs text-muted-foreground truncate">{j.company}</div></div>
                      <button onClick={()=>remove(j.id)} className="text-muted-foreground/60 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={12}/></button>
                    </div>
                    {j.salary && <div className="font-mono text-xs text-emerald-400 mt-1" data-testid={`job-salary-${j.id}`}>{ready ? <Money value={j.salary} from={currency} /> : "—"}</div>}
                    {j.location && <div className="text-xs text-muted-foreground mt-1">{j.location}</div>}
                    <div className="flex items-center gap-2 mt-2">
                      <select data-testid={`job-status-${j.id}`} value={j.status} onChange={e=>moveStatus(j, e.target.value)} className="text-xs bg-card border border-border px-2 py-0.5 flex-1">
                        {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                      {j.job_url && <a href={j.job_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground"><ExternalLink size={12}/></a>}
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div className="text-xs text-muted-foreground/60 text-center py-4">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
      {open && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="add-job-modal">
          <form onSubmit={add} className="card-flat p-6 max-w-xl w-full space-y-4">
            <div className="flex justify-between items-center">
              <div className="font-display font-bold text-2xl tracking-tight">Add job application</div>
              <button type="button" onClick={()=>setOpen(false)}><X size={18}/></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Company" value={form.company} onChange={v=>setForm({...form, company:v})} required testId="jf-company"/>
              <Field label="Role" value={form.role} onChange={v=>setForm({...form, role:v})} required testId="jf-role"/>
              <Field label="Location" value={form.location} onChange={v=>setForm({...form, location:v})} testId="jf-location"/>
              <Field label={`Salary (${currency})`} type="number" value={form.salary} onChange={v=>setForm({...form, salary:v})} testId="jf-salary"/>
              <div className="col-span-2"><Field label="Job URL" value={form.job_url} onChange={v=>setForm({...form, job_url:v})} testId="jf-url"/></div>
              <div className="col-span-2">
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Notes</label>
                <textarea data-testid="jf-notes" value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} className="w-full mt-2 bg-card border border-border px-4 py-3 min-h-[80px]"/>
              </div>
            </div>
            <button data-testid="jf-submit" className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors w-full py-3">Add to tracker</button>
          </form>
        </div>
      )}
    </>
  );
}

function Matches() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { currency, ready } = useCurrency();
  useEffect(() => { load(); }, []);
  // AI matches store amounts in USD — refetch on currency change is unnecessary
  // (the frontend Money component converts on display), but we DO expose the
  // change so cached views re-render with the new formatting.
  useCurrencyChangeListener(() => { /* Money component reads useCurrency() */ });
  const load = async () => { setLoading(true); try { const r = await api.get("/jobs/matches"); setItems(r.data); } catch { toast.error("Complete your profile first"); } finally { setLoading(false); } };
  const refresh = async () => { setRefreshing(true); try { const r = await api.post("/jobs/matches/refresh"); setItems(r.data); toast.success("Fresh matches"); } catch { toast.error("Refresh failed"); } finally { setRefreshing(false); } };

  const trackIt = async (m) => {
    try {
      await api.post("/jobs", { company: m.company, role: m.role, location: m.location, salary: m.salary_max, status: "applied", notes: `AI match · ${m.match_score}% fit · ${m.fit_summary}` });
      toast.success("Added to tracker");
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="card-flat p-10 text-center text-muted-foreground">Finding matches...</div>;
  if (!items || items.length === 0) return (
    <div className="card-flat p-10 text-center">
      <div className="text-muted-foreground mb-4">No matches yet. Complete your profile to unlock AI job matches.</div>
      <button data-testid="matches-generate" onClick={refresh} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-6 py-2.5">Generate matches</button>
    </div>
  );

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-muted-foreground">{items.length} personalized matches from Claude 4.5</div>
        <button data-testid="matches-refresh" onClick={refresh} disabled={refreshing} className="inline-flex items-center justify-center rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors px-4 py-2 text-sm flex items-center gap-2"><RefreshCw size={14} className={refreshing?"animate-spin":""}/> Refresh</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4" data-testid="matches-grid">
        {items.map((m, i) => (
          <div key={m.id || i} className="card-flat p-5" data-testid={`match-${i}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="min-w-0">
                <div className="font-display font-bold text-lg tracking-tight truncate">{m.role}</div>
                <div className="text-sm text-muted-foreground truncate">{m.company} · {m.location}</div>
              </div>
              <ScoreBadge score={m.match_score}/>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="text-xs font-mono text-emerald-400" data-testid={`match-salary-${i}`}>
                {ready ? (
                  <>
                    <Money value={m.salary_min} from={m.currency || "USD"} /> — <Money value={m.salary_max} from={m.currency || "USD"} />
                  </>
                ) : "—"}
              </div>
              <div className="text-[10px] font-mono uppercase text-muted-foreground">Posted {m.posted_days_ago}d ago</div>
            </div>
            <div className="text-sm text-foreground/80 mb-4">{m.fit_summary}</div>
            <div className="mb-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Match reasons</div>
              <ul className="space-y-1">
                {(m.match_reasons||[]).slice(0,3).map((r,j)=> <li key={j} className="text-xs text-muted-foreground flex gap-1.5"><Target size={10} className="text-emerald-400 mt-1 shrink-0"/> {r}</li>)}
              </ul>
            </div>
            {m.missing && m.missing.length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Gaps</div>
                <div className="flex flex-wrap gap-1">{m.missing.map((x,j)=> <span key={j} className="text-[10px] bg-amber-500/10 border border-amber-500/30 text-amber-300 px-2 py-0.5">{x}</span>)}</div>
              </div>
            )}
            <button data-testid={`match-track-${i}`} onClick={()=>trackIt(m)} className="inline-flex items-center justify-center rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors w-full py-2 text-xs">Track in pipeline</button>
          </div>
        ))}
      </div>
    </>
  );
}

function ScoreBadge({ score }) {
  const color = score >= 90 ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : score >= 75 ? "bg-primary/10 border-primary/40 text-[#5c93ff]" : "bg-amber-500/10 border-amber-500/30 text-amber-300";
  return <div className={`shrink-0 px-2 py-1 border ${color} font-mono font-bold text-sm`}>{score}%</div>;
}

function Field({ label, value, onChange, type="text", required, testId }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
      <input data-testid={testId} required={required} type={type} value={value} onChange={e=>onChange(e.target.value)} className="w-full mt-2 bg-card border border-border px-4 py-3 focus:outline-none focus:border-primary"/>
    </div>
  );
}
